import React, { useState, useContext, useEffect } from "react";
import {
  Box,
  Container,
  IconButton,
  Typography,
  Button,
  Modal,
  Zoom,
  useTheme,
  List,
  ListItem,
  Snackbar,
  Alert,
  Divider,
  CircularProgress,
} from "@mui/material";
import { styled, Theme } from "@mui/system";
import { AiOutlineClose } from "react-icons/ai";
import { MdFileDownload } from "react-icons/md";
import Navbar from '../components/Navbar';
import { ThemeContext } from '../contexts/ThemeContext';
import Footer from '../components/Footer';
import { Global, css } from '@emotion/react';
import { brandPalette } from '../theme/brand';

// Az API alap-címe: éles oldalon üres (az nginx továbbítja az /api kéréseket), fejlesztéskor a REACT_APP_API_URL-ből jön
const API = process.env.REACT_APP_API_URL ?? "";

interface ThemeItem {
  id: "coloringBooks" | "worksheets" | "boardGames";
  title: string;
  image: string;
  description: string;
  position: { top: string | number; left?: string | number; right?: string | number; };
}

// A backendről érkező adatoknak megfelelő interface
interface DownloadedContentData {
  id: number;
  name: string;
  size: number; // méret bájtokban
  typeName: string; // A backendből érkező "typeName"
}

const CircularImage = styled("div")(({ theme }: { theme: Theme }) => ({
  width: "250px",
  height: "250px",
  borderRadius: "50%",
  overflow: "hidden",
  cursor: "pointer",
  transition: "all 0.3s ease",
  position: "relative",
  boxShadow: "0 10px 20px rgba(0,0,0,0.2)",
  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: "0 15px 30px rgba(0,0,0,0.3)"
  }
}));

const ImageContainer = styled(Box)({
  position: "relative",
  width: "100%",
  height: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

const DownloadSection = styled(Box)({
  marginTop: "24px",
  borderRadius: "16px"
});

const LetolthetoTartalmak = () => {
  const [selectedTheme, setSelectedTheme] = useState<ThemeItem | null>(null);
  const [open, setOpen] = useState(false);
  const [downloadingItem, setDownloadingItem] = useState<number | null>(null);
  const [allDownloadableContent, setAllDownloadableContent] = useState<DownloadedContentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalLoading, setModalLoading] = useState(false);
  const { darkMode } = useContext(ThemeContext)!;
  const theme = useTheme();
  const pal = brandPalette(darkMode);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");

  const themes: ThemeItem[] = [
    {
      id: "coloringBooks",
      title: "Színezőkönyvek",
      image: "https://images.unsplash.com/photo-1513542789411-b6a5d4f31634",
      description: "Fedezd fel színezőkönyveink varázslatos világát, tele aranyos állatokkal, gyönyörű tájakkal és fantasztikus lényekkel! Fejleszd kreativitásodat és kapcsolódj ki a színek erejével. Ideális gyermekeknek és felnőtteknek egyaránt.",
      position: { top: '0%', left: '0%' }
    },
    {
      id: "worksheets",
      title: "Munkalapok",
      image: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b",
      description: "Oktató és szórakoztató munkalapjaink segítenek a tanulásban. Gyakorold a matematikát, a tudományt és a nyelvtant interaktív feladatokkal, amelyek játékosan fejlesztik a készségeidet. Tökéletes kiegészítő otthoni és iskolai oktatáshoz.",
      position: { top: '35%', right: '0%' }
    },
    {
      id: "boardGames",
      title: "Társasjátékok",
      image: "https://images.unsplash.com/photo-1611996575749-79a3a250f948",
      description: "Készülj fel a családi szórakozásra letölthető társasjátékainkkal! Stratégiai játékok, kalandos küldetések és klasszikus kedvencek várnak rátok. Nyomtassátok ki, vágjátok ki, és indulhat a játék a barátokkal vagy a családdal!",
      position: { top: '70%', left: '0%' }
    }
  ];

  useEffect(() => {
    fetch(`${API}/api/downloadable/all`)
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        setAllDownloadableContent(data);
      })
      .catch(error => {
        console.error("Failed to fetch all downloadable content:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleImageClick = (themeItem: ThemeItem) => {
    setSelectedTheme(themeItem);
    setOpen(true);
    setModalLoading(false);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedTheme(null);
    setDownloadingItem(null);
  };

  const handleDownload = async (item: DownloadedContentData) => {
    setDownloadingItem(item.id);

    try {
      await new Promise(resolve => setTimeout(resolve, 3000));

      const response = await fetch(`${API}/api/downloadable/download/${item.id}`);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = item.name.toLowerCase().endsWith(".png") ? item.name : `${item.name}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setSnackbarMessage("Sikeres letöltés!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

    } catch (error) {
      console.error("Download failed:", error);
      setSnackbarMessage("A letöltés sikertelen volt.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setDownloadingItem(null);
    }
  };

  const formatSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const typeNameToThemeId: Record<string, ThemeItem["id"]> = {
    "Munkalap": "worksheets",
    "Színező": "coloringBooks",
    "Társasjáték": "boardGames"
  };

  const filteredContent = allDownloadableContent.filter(
    item => typeNameToThemeId[item.typeName] === selectedTheme?.id
  );

  return (
    <>
      <Global
        styles={css`
          body {
            scrollbar-gutter: stable both-sides;
          }
        `}
      />
      <Navbar />
      <Container
        maxWidth={false}
        sx={{
          py: 4,
          pt: '80px',
          backgroundColor: 'transparent',
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          px: { xs: 2, sm: 3, md: 4 }
        }}
      >
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            textAlign: 'center',
            mb: 4,
            fontWeight: 'bold',
            color: pal.heading,
            width: '100%'
          }}
        >
          Letölthető tartalmak
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '600px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              minHeight: '600px',
              gap: theme.spacing(6),
              [theme.breakpoints.up('md')]: {
                minHeight: 'auto',
              }
            }}
          >
            {themes.map((themeItem: ThemeItem, index) => (
              <Box
                key={themeItem.id}
                sx={{
                  display: 'flex',
                  flexDirection: {
                    xs: 'column',
                    md: index % 2 === 0 ? 'row' : 'row-reverse'
                  },
                  alignItems: 'center',
                  gap: theme.spacing(4),
                  width: { xs: '100%', md: '70%' },
                  textAlign: { xs: 'center', md: index % 2 === 0 ? 'left' : 'right' },
                }}
              >
                <CircularImage onClick={() => handleImageClick(themeItem)}>
                  <ImageContainer>
                    <img
                      src={themeItem.image}
                      alt={themeItem.title}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover"
                      }}
                      loading="lazy"
                    />
                    <Typography
                      variant="h5"
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        backdropFilter: "blur(4px)",
                        color: "#fff",
                        padding: "10px",
                        textAlign: "center",
                        fontWeight: "bold",
                        width: '100%',
                        boxSizing: 'border-box',
                      }}
                    >
                      {themeItem.title}
                    </Typography>
                  </ImageContainer>
                </CircularImage>
                <Box sx={{ maxWidth: '500px' }}>
                  <Typography variant="h4" gutterBottom sx={{ color: pal.heading, fontWeight: 'bold' }}>
                    {themeItem.title}
                  </Typography>
                  <Typography variant="body1" sx={{ color: pal.text, fontSize: '1.1rem', lineHeight: 1.6 }}>
                    {themeItem.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        <Modal
          open={open}
          onClose={handleClose}
          closeAfterTransition
          disableScrollLock
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(8px)", // Háttér elhomályosítása
          }}
        >
          <Zoom in={open}>
            <Box
              sx={{
                // 3D Neumorphism hátterek
                background: darkMode 
                  ? 'linear-gradient(145deg, #1d1247, #120A39)' 
                  : 'linear-gradient(145deg, #ffffff, #F7F0F9)',
                color: pal.text,
                borderRadius: "24px", // Lágyabb, kerekebb ablak
                p: { xs: 3, md: 5 },
                width: { xs: '90%', sm: '80%', md: '70%' },
                maxWidth: '850px',
                outline: 'none',
                position: 'relative',
                maxHeight: '90vh',
                overflowY: 'auto',
                // Térbeli árnyék a teljes felugró ablaknak
                boxShadow: darkMode 
                  ? '0 20px 60px rgba(0, 0, 0, 0.6)' 
                  : '0 20px 60px rgba(109, 27, 119, 0.25)',
                border: pal.cardBorder,
              }}
            >
              {/* Kiemelkedő Lebegő Bezárás Gomb */}
              <IconButton
                onClick={handleClose}
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  color: pal.heading,
                  background: darkMode ? '#2a1a66' : '#ffffff',
                  boxShadow: darkMode 
                    ? '0 4px 12px rgba(0, 0, 0, 0.5)' 
                    : '0 4px 12px rgba(109, 27, 119, 0.25)',
                  '&:hover': {
                    transform: 'scale(1.1) translateY(-2px)',
                    background: darkMode ? '#3b2590' : '#F7F0F9',
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: '50%',
                  zIndex: 10,
                }}
              >
                <AiOutlineClose />
              </IconButton>

              {selectedTheme && (
                <>
                  <Box sx={{ mb: 4, mt: 2 }}>
                    <img
                      src={selectedTheme.image}
                      alt={selectedTheme.title}
                      style={{
                        width: "100%",
                        maxHeight: "350px",
                        objectFit: "cover",
                        borderRadius: "16px",
                        // A kép árnyéka miatt lebegő hatású lesz
                        boxShadow: darkMode 
                          ? "0 15px 35px rgba(0,0,0,0.6)" 
                          : "0 15px 35px rgba(0,0,0,0.2)",
                      }}
                      loading="lazy"
                    />
                  </Box>

                  <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', color: pal.heading, textAlign: 'center' }}>
                    {selectedTheme.title}
                  </Typography>

                  <DownloadSection sx={{ p: { xs: 0, sm: 2 } }}>
                    <Typography variant="h6" gutterBottom sx={{ color: pal.muted, mb: 3, ml: 1 }}>
                      Válassz a letölthető fájlok közül:
                    </Typography>
                    
                    {modalLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <List sx={{ pt: 0, px: { xs: 0, sm: 1 } }}>
                        {filteredContent.length > 0 ? (
                          filteredContent.map((item: DownloadedContentData, index: number) => (
                            <ListItem
                              key={item.id}
                              disablePadding
                              sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                justifyContent: 'space-between',
                                alignItems: { xs: 'center', sm: 'center' },
                                py: 2.5,
                                px: 3,
                                mb: 2.5,
                                borderRadius: "16px",
                                // 3D-s Kártya stílus a listaelemeknek
                                background: darkMode 
                                  ? 'rgba(255, 255, 255, 0.06)' 
                                  : '#FBF5FC',
                                boxShadow: darkMode 
                                  ? '0 4px 14px rgba(0, 0, 0, 0.35)' 
                                  : '0 4px 14px rgba(109, 27, 119, 0.12)',
                                transition: 'all 0.3s ease',
                                border: pal.cardBorder,
                                '&:hover': {
                                  transform: 'translateY(-5px)',
                                  boxShadow: darkMode 
                                    ? '0 10px 24px rgba(0, 0, 0, 0.5)' 
                                    : '0 10px 24px rgba(109, 27, 119, 0.22)',
                                }
                              }}
                            >
                              <Box sx={{ flexGrow: 1, mb: { xs: 2, sm: 0 }, textAlign: { xs: 'center', sm: 'left' } }}>
                                <Typography variant="h6" sx={{ color: pal.heading, fontWeight: 600 }}>
                                  {item.name}
                                </Typography>
                                <Typography variant="body2" sx={{ color: pal.muted, mt: 0.5 }}>
                                  Méret: <strong>{formatSize(item.size)}</strong>
                                </Typography>
                              </Box>

                              <Button
                                variant="contained"
                                startIcon={downloadingItem === item.id ? null : <MdFileDownload />}
                                onClick={() => handleDownload(item)}
                                disabled={downloadingItem === item.id}
                                sx={{
                                  // Gradiens, kiemelkedő Letöltés Gomb
                                  background: 'linear-gradient(45deg, #C92BB5 30%, #6D1B77 90%)',
                                  borderRadius: '30px',
                                  color: '#fff',
                                  fontWeight: 'bold',
                                  textTransform: 'none',
                                  fontSize: '1rem',
                                  padding: '10px 28px',
                                  boxShadow: '0 5px 15px rgba(201, 43, 181, 0.4)',
                                  transition: 'all 0.3s ease-in-out',
                                  minWidth: '140px',
                                  '&:hover': {
                                    background: 'linear-gradient(45deg, #6D1B77 30%, #C92BB5 90%)',
                                    transform: 'translateY(-3px)',
                                    boxShadow: '0 8px 20px rgba(201, 43, 181, 0.6)',
                                  },
                                  '&.Mui-disabled': {
                                    background: darkMode ? '#555' : '#ccc',
                                    color: darkMode ? '#888' : '#fff',
                                    boxShadow: 'none',
                                    transform: 'none',
                                  },
                                }}
                              >
                                {downloadingItem === item.id ? (
                                  <CircularProgress size={24} color="inherit" />
                                ) : (
                                  'Letöltés'
                                )}
                              </Button>
                            </ListItem>
                          ))
                        ) : (
                          <Typography variant="body1" sx={{ textAlign: 'center', p: 4, color: pal.muted, fontStyle: 'italic' }}>
                            Nincsenek letölthető tartalmak ebben a kategóriában.
                          </Typography>
                        )}
                      </List>
                    )}
                  </DownloadSection>
                </>
              )}
            </Box>
          </Zoom>
        </Modal>
      </Container>
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%", borderRadius: '12px', boxShadow: '0 10px 20px rgba(0,0,0,0.2)' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Footer />
    </>
  );
};

export default LetolthetoTartalmak;